import { Component, ElementRef, HostListener, Injector, ViewChild, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { AppComponentBase } from "@shared/common/app-component-base";
import { AppMenuItem } from "../nav/app-menu-item";
import { AppNavigationService } from "../nav/app-navigation.service";

/** A page the user may open, with the words it is found by. */
interface PageSearchEntry {
    label: string;
    /** The menu groups above it, e.g. "Back office › Inventory". */
    trail: string;
    route: string;
    icon: string;
    normalizedLabel: string;
    normalizedText: string;
}

/**
 * "Find a page" in the header: searches the menu pages the user may open (the sidebar's own permission rules) by their
 * localized names and groups, client-side. Ctrl+K / ⌘K focuses it; arrows, Enter and Escape work in the list.
 */
@Component({
    templateUrl: "./page-search.component.html",
    styleUrls: ["./page-search.component.less"],
    selector: "page-search",
    encapsulation: ViewEncapsulation.None,
})
export class PageSearchComponent extends AppComponentBase {
    @ViewChild("searchInput") searchInput: ElementRef;

    readonly listId = "pm-page-search-list";
    readonly maxResults = 8;
    readonly shortcutLabel = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl K";

    query = "";
    open = false;
    results: PageSearchEntry[] = [];
    activeIndex = -1;

    private entries: PageSearchEntry[] = null;

    constructor(
        injector: Injector,
        private _appNavigationService: AppNavigationService,
        private _router: Router
    ) {
        super(injector);
    }

    @HostListener("document:keydown", ["$event"])
    onDocumentKeydown(event: KeyboardEvent): void {
        if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && (event.key === "k" || event.key === "K")) {
            event.preventDefault();
            const input = this.searchInput.nativeElement as HTMLInputElement;
            input.focus();
            input.select();
        }
    }

    onFocus(): void {
        this.search();
    }

    onQueryChange(): void {
        this.search();
    }

    onKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                this.moveActive(1);
                break;
            case "ArrowUp":
                event.preventDefault();
                this.moveActive(-1);
                break;
            case "Enter":
                if (this.open && this.activeIndex >= 0) {
                    event.preventDefault();
                    this.go(this.results[this.activeIndex]);
                }
                break;
            case "Escape":
                if (this.open || this.query) {
                    event.preventDefault();
                    this.query = "";
                    this.close();
                }
                break;
        }
    }

    close(): void {
        this.open = false;
        this.activeIndex = -1;
    }

    go(entry: PageSearchEntry): void {
        this.query = "";
        this.close();
        (this.searchInput.nativeElement as HTMLInputElement).blur();
        this._router.navigate([entry.route]);
    }

    private moveActive(step: number): void {
        if (!this.open || !this.results.length) {
            return;
        }
        this.activeIndex = (this.activeIndex + step + this.results.length) % this.results.length;
    }

    /**
     * Every word of the query must appear in the page name or its groups. Ranking: the whole query as the name, then
     * names starting with it, then names containing it, then group-only matches; shorter names first within a rank.
     */
    private search(): void {
        const query = PageSearchComponent.normalize(this.query);
        const words = query.split(" ").filter((w) => !!w);
        if (!words.length) {
            this.results = [];
            this.close();
            return;
        }
        const rankOf = (entry: PageSearchEntry) => {
            const at = entry.normalizedLabel.indexOf(query);
            return entry.normalizedLabel === query ? 0 : at === 0 ? 1 : at > 0 ? 2 : 3;
        };
        this.results = this.getEntries()
            .filter((entry) => words.every((w) => entry.normalizedText.indexOf(w) >= 0))
            .map((entry) => ({ entry: entry, rank: rankOf(entry) }))
            .sort((a, b) =>
                a.rank - b.rank ||
                a.entry.label.length - b.entry.label.length ||
                a.entry.label.localeCompare(b.entry.label))
            .slice(0, this.maxResults)
            .map((r) => r.entry);
        this.open = true;
        this.activeIndex = this.results.length ? 0 : -1;
    }

    /** Built once, on first use, from the same menu and visibility rules as the sidebar. */
    private getEntries(): PageSearchEntry[] {
        if (this.entries) {
            return this.entries;
        }
        const isParent = this._appNavigationService.isCurrentTenantParent();
        const entries: PageSearchEntry[] = [];
        const seen: { [route: string]: boolean } = {};
        const visit = (items: AppMenuItem[], groups: string[]) =>
            items.forEach((item) => {
                if (!this._appNavigationService.isMenuItemVisible(item, isParent)) {
                    return;
                }
                const label = this.l(item.name);
                if (item.items && item.items.length) {
                    visit(item.items, groups.concat(label));
                    return;
                }
                if (!item.route || seen[item.route]) {
                    return;
                }
                seen[item.route] = true;
                const trail = groups.join(" › ");
                entries.push({
                    label: label,
                    trail: trail,
                    route: item.route,
                    icon: item.icon,
                    normalizedLabel: PageSearchComponent.normalize(label),
                    normalizedText: PageSearchComponent.normalize(label + " " + trail),
                });
            });
        visit(this._appNavigationService.getMenu().items, []);
        this.entries = entries;
        return entries;
    }

    /** Case, Arabic letter variants (أ إ آ ا, ة ه, ى ي), diacritics and tatweel do not matter. */
    private static normalize(text: string): string {
        return (text || "")
            .toLowerCase()
            .replace(/[ً-ٰٟـ]/g, "")
            .replace(/[أإآ]/g, "ا")
            .replace(/ة/g, "ه")
            .replace(/ى/g, "ي")
            .replace(/\s+/g, " ")
            .trim();
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/layout/page-search/page-search.component.ts